/**
 * Audio Manager Module
 * Handles all audio functionality including sound effects and background music
 */

class AudioManager {
    constructor() {
        this.audioContext = null;
        this.masterVolume = 0.3; // Default volume
        this.isMuted = false;
        this.sounds = new Map();
        this.backgroundMusic = null;
        this.musicVolume = 0.1;
        this.currentlyPlayingSources = new Set(); // Track currently playing sound sources

        this.init();
    }

    /**
     * Initialize the audio context
     */
    async init() {
        try {
            // Create audio context with fallback for Safari
            const AudioContext = window.AudioContext || window.webkitAudioContext;
            this.audioContext = new AudioContext();

            // Resume context on user interaction (required by browsers)
            const resumeAudio = () => {
                if (this.audioContext.state === 'suspended') {
                    this.audioContext.resume();
                }
                // Remove the event listener after first interaction
                document.removeEventListener('click', resumeAudio);
                document.removeEventListener('keydown', resumeAudio);
                document.removeEventListener('touchstart', resumeAudio);
            };

            // Add multiple event listeners to ensure we catch the first interaction
            document.addEventListener('click', resumeAudio, { once: true });
            document.addEventListener('keydown', resumeAudio, { once: true });
            document.addEventListener('touchstart', resumeAudio, { once: true });

        } catch (error) {
            console.warn('Audio context not supported:', error);
        }
    }

    /**
     * Load a sound effect
     * @param {string} name - Name to reference the sound by
     * @param {string} url - URL of the audio file
     */
    async loadSound(name, url) {
        if (this.sounds.has(name)) {
            return; // Already loaded
        }

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const arrayBuffer = await response.arrayBuffer();
            const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
            this.sounds.set(name, audioBuffer);
        } catch (error) {
            console.warn(`Failed to load sound ${name} from ${url}:`, error);
            throw error; // Re-throw to allow handling by caller
        }
    }

    /**
     * Stop all currently playing sound effects
     */
    stopAllSounds() {
        this.currentlyPlayingSources.forEach(source => {
            try {
                source.stop();
                source.disconnect();
            } catch (e) {
                // Source might already be stopped
                console.debug('Error stopping audio source:', e);
            }
        });
        this.currentlyPlayingSources.clear();
    }

    /**
     * Play a sound effect
     * @param {string} name - Name of the sound to play
     * @param {Object} options - Playback options
     * @param {number} [options.volume=1] - Volume multiplier (0 to 1)
     * @param {number} [options.playbackRate=1] - Playback rate
     * @returns {AudioBufferSourceNode|null} The audio source node or null if failed
     */
    playSound(name, options = {}) {
        if (!this.audioContext || this.isMuted) return null;

        const buffer = this.sounds.get(name);
        if (!buffer) {
            console.warn(`Sound "${name}" not loaded`);
            return null;
        }

        // Stop any currently playing sounds if needed
        if (options.stopOthers !== false) {
            this.stopAllSounds();
        }

        try {
            const source = this.audioContext.createBufferSource();
            source.buffer = buffer;

            const gainNode = this.audioContext.createGain();
            gainNode.gain.value = (options.volume || 1) * this.masterVolume;

            source.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            // Set playback rate if specified
            if (options.playbackRate) {
                source.playbackRate.value = options.playbackRate;
            }

            source.start();

            // Track this source
            this.currentlyPlayingSources.add(source);

            // Clean up when finished
            source.onended = () => {
                source.disconnect();
                gainNode.disconnect();
                this.currentlyPlayingSources.delete(source);
            };

            return source;
        } catch (error) {
            console.error('Error playing sound:', error);
            return null;
        }
    }

    /**
     * Play a sound effect and return a promise that resolves when it ends
     * @param {string} name - Name of the sound to play
     * @param {Object} options - Playback options
     * @returns {Promise<void>} Resolves when the sound finishes playing
     */
    playSoundAndWait(name, options = {}) {
        return new Promise((resolve) => {
            if (!this.audioContext || this.isMuted) {
                resolve();
                return;
            }

            const source = this.playSound(name, { ...options, stopOthers: false });
            if (!source) {
                resolve();
                return;
            }

            // Replace the onended handler to resolve the promise
            const originalOnEnded = source.onended;
            source.onended = (...args) => {
                if (originalOnEnded) originalOnEnded.apply(source, args);
                resolve();
            };
        });
    }

    /**
     * Set master volume for all sounds
     * @param {number} volume - Volume level (0 to 1)
     */
    setMasterVolume(volume) {
        this.masterVolume = Math.max(0, Math.min(1, volume));
        if (this.backgroundMusic) {
            this.backgroundMusic.volume = this.musicVolume * this.masterVolume;
        }
    }

    /**
     * Toggle mute state
     * @returns {boolean} New mute state
     */
    toggleMute() {
        this.isMuted = !this.isMuted;
        if (this.backgroundMusic) {
            this.backgroundMusic.muted = this.isMuted;
        }
        return this.isMuted;
    }

    /**
     * Set the background music
     * @param {HTMLAudioElement} audioElement - Audio element to use for background music
     */
    setBackgroundMusic(audioElement) {
        if (this.backgroundMusic) {
            this.backgroundMusic.pause();
            this.backgroundMusic = null;
        }

        this.backgroundMusic = audioElement;
        if (this.backgroundMusic) {
            this.backgroundMusic.volume = this.musicVolume * this.masterVolume;
            this.backgroundMusic.loop = true;
            this.backgroundMusic.muted = this.isMuted;
        }
    }

    /**
     * Preload all guitar sound effects
     */
    async preloadGuitarSounds() {
        const sounds = [
            { name: 'fiez-vous-pas-aux-guitares', url: '/assets/sounds/effects/Fiez vous pas aux guitares_loud.mp3' },
            { name: 'sdgdr', url: '/assets/sounds/effects/SDGDR_loud.mp3' },
            { name: 'sdgdr-com', url: '/assets/sounds/effects/SDGDR.com_loud.mp3' },
            { name: 'de-l-argent', url: '/assets/sounds/effects/De L\'argent_loud.mp3' },
            { name: 'l-argent-va-aux-itinerants', url: '/assets/sounds/effects/L\'argent va aux itinerants_loud.mp3' },
            { name: 'la-promesse', url: '/assets/sounds/effects/La promesse_loud.mp3' },
            { name: 'des-petites-couvertes', url: '/assets/sounds/effects/Des petites couvertes_loud.mp3' },
            { name: 'si-vous-voulez', url: '/assets/sounds/effects/Si vous voulez_loud.mp3' },
            { name: 'fiez-vous-pas-a-la-mise-en-page', url: '/assets/sounds/effects/Fiez vous pas a la mise en page_loud.mp3' },
            { name: 'la-journee', url: '/assets/sounds/effects/La journee_loud.mp3' },
            { name: 'qui-a-laisse-sortir-short', url: '/assets/sounds/effects/Qui a laissé sortir short.mp3' },
            { name: 'reception', url: '/assets/sounds/effects/Reception_loud.mp3' },
            { name: 'sortir-les-chiens-short', url: '/assets/sounds/effects/Sortir les chiens short.mp3' },
            { name: 'merci', url: '/assets/sounds/effects/Merci_loudlong.mp3' },
            { name: 'sirenes', url: '/assets/sounds/effects/Sirenes_loud.mp3' },
            { name: 'un-service-en-attire-un-autre', url: '/assets/sounds/effects/Un service en attire un autre_loud.mp3' },
            { name: 'poussiere-d-ange', url: '/assets/sounds/effects/Poussiere d\'ange_loud.mp3' },
            { name: 'il-connait-les-accords', url: '/assets/sounds/effects/Il connait les accords_loud.mp3' },
            { name: 'petit-chaperon-rouge', url: '/assets/sounds/effects/Le petit chaperon rouge_loud.mp3' },
            { name: 'montagne-sirop', url: '/assets/sounds/MontagneSirop.mp3' },
            { name: 'ami', url: '/assets/sounds/effects/Ami.mp3' },
            { name: 'roi', url: '/assets/sounds/effects/Roi.mp3' },
            { name: 'speech-finissants', url: '/assets/sounds/effects/Speech Finissants.mp3' },
            { name: 'aventures', url: '/assets/sounds/effects/Aventures.mp3' },
            { name: 'biz', url: '/assets/sounds/effects/Biz Bizou.mp3' },
            { name: 'jeune-gothique', url: '/assets/sounds/effects/Jeune Gothique.mp3' },
            { name: 'fresh', url: '/assets/sounds/effects/Look Fresh.mp3' },
        ];

        // Load sounds in parallel but with a small delay between batches to avoid overwhelming the browser
        const BATCH_SIZE = 3;
        for (let i = 0; i < sounds.length; i += BATCH_SIZE) {
            const batch = sounds.slice(i, i + BATCH_SIZE);
            await Promise.allSettled(
                batch.map(sound => this.loadSound(sound.name, sound.url))
            );
        }
    }
}

// Create and export a singleton instance
export const audioManager = new AudioManager();

// Also export the class for potential extension
export default AudioManager;
